import { useAuth } from '@agentnet/auth';
import { DataTable, pxToRem } from '@fluentsms/agentnet-web-components';
import { Card, CardContent, Divider, Grid } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { CellClickedEvent, ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { getEndorsementsByPolicyDate } from 'api/jacket-api';
import { getCplTypesApi, getPolicyInventoryApi } from 'api/orderManagement/order-management-api';
import { format as formatDate, isValid, parse } from 'date-fns';
import { StatCodePdfWindowName, WindowFeatures } from 'features/jackets/constants';
import Endorsements from 'features/jackets/Endorsements';
import { Endorsement, PolicyField, PolicyFieldOption } from 'features/jackets/types';
import { PolicyInventoryType } from 'features/orderManagement/types';
import { useNavPermissions } from 'hooks/useNavPermissions';
import { useEffect, useState } from 'react';
import AgentNetDivider from 'ui-kit/components/dividers/AgentNetDivider2';
import QuestionItem from 'ui-kit/components/tables/AdditionalQuestionItem';
import { DeleteOutlineIcon } from 'ui-kit/icons/DeleteOutlineIcon';
import { AgentNetCheckBox, CurrencyField } from 'ui-kit/inputs';
import AgentNetDropdownSelector, { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import { makeRandomId } from 'utilities/utilities';
import { CalcCriteriaStandaloneSchema } from 'utilities/validation/schemas/jacket-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import { CurrencyEditor, CurrencyEditorStyle } from '../../../features/orderManagement/CurrencyEditor';
import useAsync from '../../../hooks/useAsync';
import NewMexicoStatCodePdf from '../../../static/Stat_Code_New_Mexico_State.pdf';
import NewYorkStatCodePdf from '../../../static/Stat_Code_New_York_State.pdf';
import TexasStatCodePdf from '../../../static/Stat_Code_Texas_State.pdf';
import AgentNetButton from '../../../ui-kit/components/button/AgentNetButton';
import DateFieldString from '../../../ui-kit/inputs/DateField/DateFieldString';
import CPLCalcTable from './CPLCalcTable';
import Policy from './Policy';
import VirtualJacketCard, { VirtualJacket } from './VirtualJacketCard';

// Interface for stat code dropdown options
interface StatCodeOption {
  name: string;
  value: string;
}

export interface CplType {
  cplGroupId: number;
  cplGroupDesc: string;
  cplId: number;
  coveredPartyTypeCdId: number;
  cplDesc: string;
  starsItemNumber: string;
  stateCode: string;
  underwriterId: number;
  fee?: number;
  feeInput?: string;
  [id: string]: any | undefined;
}

interface CalcCriteriaTabProps {
  calcCriteriaData: any;
  updateCalcCriteriaData: any;
  fileLevelValidationError: any;
  disableFields?: boolean;
  savedJacketData: any;
  updateJacketEndorsement: (
    jacketId: string | null,
    randomId: string | null,
    rflId: number | null,
    name: string | null,
    amount: number | null,
    reason: string,
    type: string,
    starsNumber: string | null,
    index: number,
  ) => void;
  updateSaEndorsement: (
    jacketId: string | null,
    id: string | null,
    rflId: number | null,
    name: string | null,
    amount: number | null,
    reason: string,
    type: string,
    starsNumber: string | null,
  ) => void;
  filterStatCode: (statCode: PolicyField) => { newValue: string; filteredOption: StatCodeOption[] };
  additionalQuestionRef: any;
  validationErrors: FieldValidationError[];
  shouldShowValidation: boolean;
  cplFees: CplType[];
  setCplFees: (cplFees: CplType[]) => void;
  onValidationChange?: (
    seErrors: FieldValidationError[],
    cplErrors: FieldValidationError[],
    virtualJacketErrors: FieldValidationError[],
  ) => void;
  virtualJackets?: VirtualJacket[];
  setVirtualJackets?: (jackets: VirtualJacket[]) => void;
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      margin: theme.spacing(2, 1.25, 0, 1),
    },
    heading: {
      margin: theme.spacing(1, 0, 2),
    },
    marginZero: {
      margin: 0,
    },
    paddingTopZero: {
      paddingTop: '0 !important',
    },
    endorsement: {
      padding: theme.spacing(2, 1, 1, 1),
    },
    sectionCard: {
      border: '1px solid #E0E0E0',
      borderRadius: pxToRem(4),
      boxShadow: 'none',
    },
    sectionCardContent: {
      padding: theme.spacing(0.5),
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    sectionTitle: {
      fontSize: pxToRem(16),
      fontWeight: 500,
      color: '#212121',
      padding: theme.spacing(1.25, 0, 1.25, 1.25),
    },
    addVirtualJacketButton: {
      position: 'relative',
      left: pxToRem(95),
      top: pxToRem(5),
    },
  }),
);

const CalcCriteriaTab = ({
  calcCriteriaData,
  updateCalcCriteriaData,
  updateJacketEndorsement,
  updateSaEndorsement,
  filterStatCode,
  additionalQuestionRef,
  shouldShowValidation,
  validationErrors,
  fileLevelValidationError,
  savedJacketData,
  cplFees,
  setCplFees,
  disableFields = false,
  onValidationChange,
  virtualJackets = [],
  setVirtualJackets,
}: CalcCriteriaTabProps): JSX.Element => {
  const classes = useStyles();
  const { getAccessToken } = useAuth();

  const [endorsementType, setEndorsementType] = useState<Endorsement[]>([]);
  const [seValidationError, setSEValidationError] = useState<FieldValidationError[]>([]);
  const [cplValidationError, setCplValidationError] = useState<FieldValidationError[]>([]);
  const [virtualJacketValidationError, setVirtualJacketValidationError] = useState<FieldValidationError[]>([]);
  const [rateEffectiveDate, setRateEffectiveDate] = useState<string>('');
  const [loadEndorsement, setLoadEndorsement] = useState<boolean>();
  const statCodeState = ['TX', 'NM', 'NY'];
  const statCodeOptionList = (index: number) => {
    const statCode = calcCriteriaData.jacketRates[index].fields.filter((x: PolicyField) => x.name === 'StatCode')[0];
    const { filteredOption } = filterStatCode(statCode);
    return filteredOption;
  };
  const [cplTypes, setCplTypes] = useState<CplType[]>([]);
  const { hasPermission } = useNavPermissions();
  const canUseAddCplFeeToFile = hasPermission.accessOrderManagement;

  // Virtual Jacket state
  const [policyInventory, setPolicyInventory] = useState<PolicyInventoryType[]>([]);
  const [showVirtualJacketButton, setShowVirtualJacketButton] = useState<boolean>(false);

  const getCplTypes = async (accountNumber: string): Promise<CplType[]> => {
    const token = await getAccessToken();
    return await getCplTypesApi(accountNumber, token);
  };

  const {
    execute: executeGetCplTypes,
    status: getCplTypesStatus,
    value: getCplTypesValue,
  } = useAsync<CplType[]>(getCplTypes, false);

  const getPolicyInventory = async (accountNumber: string): Promise<PolicyInventoryType[]> => {
    const token = await getAccessToken();
    return await getPolicyInventoryApi(accountNumber, token);
  };

  const {
    execute: executeGetPolicyInventory,
    status: getPolicyInventoryStatus,
    value: getPolicyInventoryValue,
  } = useAsync<PolicyInventoryType[]>(getPolicyInventory, false);

  const getCplTypesSelectOptions = (cplTypes: CplType[]): SelectOption[] => {
    if (!cplTypes || cplTypes.length === 0) {
      return [];
    }
    const cplTypeOptions = cplTypes.map((cplType) => ({
      value: cplType,
      name: cplType.starsItemNumber,
    }));
    return cplTypeOptions;
  };

  const openStatCodePDF = () => {
    switch (calcCriteriaData.propertyState) {
      case 'NM':
        return window.open(NewMexicoStatCodePdf, StatCodePdfWindowName, WindowFeatures);
      case 'NY':
        return window.open(NewYorkStatCodePdf, StatCodePdfWindowName, WindowFeatures);
      case 'TX':
        return window.open(TexasStatCodePdf, StatCodePdfWindowName, WindowFeatures);
    }
  };

  // Virtual jacket handlers
  const handleAddVirtualJacket = () => {
    if (!setVirtualJackets) return;
    const newJacket: VirtualJacket = {
      id: makeRandomId(),
      policyNumber: '',
      policyDescription: '',
      itemNumber: '',
      liabilityAmount: 0,
      policyDate: '',
      reportedGross: 0,
      reportedNet: 0,
      rateTypeId: '',
      statCode: '',
      priorLiabilityAmount: undefined,
      priorPolicyDate: undefined,
    };
    setVirtualJackets([...virtualJackets, newJacket]);
  };

  const handleUpdateVirtualJacket = (index: number, updatedJacket: VirtualJacket) => {
    if (!setVirtualJackets) return;
    const updated = [...virtualJackets];
    updated[index] = updatedJacket;
    setVirtualJackets(updated);
  };

  const handleDeleteVirtualJacket = (index: number) => {
    if (!setVirtualJackets) return;
    const updated = virtualJackets.filter((_, i) => i !== index);
    setVirtualJackets(updated);
  };

  const cplFeesColumnConfig: ColDef<CplType>[] = [
    {
      headerName: 'Type',
      field: 'starsItemNumber',
      maxWidth: 430,
      minWidth: 430,
      cellRenderer: (params: ICellRendererParams<CplType>) => {
        const value = cplTypes.find((cpl) => cpl.cplId === params.data?.cplId) ?? '';
        return (
          <AgentNetDropdownSelector
            label=""
            name="CplTypes"
            options={getCplTypesSelectOptions(cplTypes)}
            value={value}
            menuOption={(cplType: CplType) => {
              const foundIndex = cplFees.findIndex((cpl) => cpl.id === params.data?.id);
              if (foundIndex > -1) {
                const cplFee = {
                  ...cplFees[foundIndex],
                  ...cplType,
                  fee: params.data?.fee || 0,
                };
                cplFees[foundIndex] = cplFee;
                setCplFees([...cplFees]);
              }
            }}
            showValidation
          />
        );
      },
      editable: false,
      suppressHeaderMenuButton: true,
    },
    {
      colId: 'feeInput',
      headerName: 'Fee',
      field: 'feeInput',
      minWidth: 420,
      maxWidth: 420,
      cellRenderer: (params: ICellRendererParams<CplType>) => {
        const numericValue = Number(params.value);
        if (isNaN(numericValue) || !params.data) {
          return <div style={CurrencyEditorStyle}>$0.00</div>;
        }
        params.data.fee = Number(numericValue.toFixed(2));
        const twoPlaces = numericValue.toFixed(2).toString();
        const formatted = `$${twoPlaces.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        return <div style={CurrencyEditorStyle}>{formatted}</div>;
      },
      cellEditor: CurrencyEditor,
      onCellClicked: (params: CellClickedEvent) => {
        params.api.startEditingCell({
          rowIndex: params.rowIndex ?? 0,
          colKey: 'feeInput',
        });
      },
      type: 'rightAligned',
      editable: true, // handled by renderer
      suppressHeaderMenuButton: true,
    },
    {
      headerName: '',
      floatingFilterComponentParams: { suppressFilterButton: true },
      suppressMenu: true,
      floatingFilter: false,
      sortable: false,
      width: 60,
      cellStyle: { display: 'flex', justifyContent: 'center', cursor: 'pointer', alignItems: 'center' },
      pinned: 'right',
      cellRenderer: DeleteOutlineIcon,
      onCellClicked: (params) => {
        const { data } = params;
        const foundIndex = cplFees.findIndex((cpl) => cpl.id === data?.id);
        if (foundIndex > -1) {
          cplFees.splice(foundIndex, 1);
          setCplFees([...cplFees]);
        }
      },
      suppressHeaderMenuButton: true,
    },
  ];

  const fetchEndorsements = async (stateCode?: string, underwriterCode?: string, effectiveDate?: string) => {
    const token = await getAccessToken();
    const endorsements = await getEndorsementsByPolicyDate(
      stateCode ?? '',
      underwriterCode ?? '',
      effectiveDate ?? '',
      token,
    );
    const miscEndorsement: Endorsement = {
      endorsementName: 'Miscellaneous Endorsement',
      endorsementFormNumber: 'Miscellaneous Endorsement',
      policyCategories: [],
      policyCoverages: [],
    };
    const allEndorsements = [...endorsements, miscEndorsement];
    setEndorsementType(allEndorsements);
  };

  // Validate virtual jackets
  useEffect(() => {
    const errors: FieldValidationError[] = [];
    virtualJackets.forEach((jacket, index) => {
      if (!jacket.policyNumber) {
        errors.push({ field: `virtualJacket[${index}].policyNumber`, message: 'Policy Number is required' });
      }
      if (!jacket.liabilityAmount || jacket.liabilityAmount <= 0) {
        errors.push({ field: `virtualJacket[${index}].liabilityAmount`, message: 'Liability Amount is required' });
      }
      if (!jacket.policyDate) {
        errors.push({ field: `virtualJacket[${index}].policyDate`, message: 'Policy Date is required' });
      }
      if (!jacket.reportedGross || jacket.reportedGross <= 0) {
        errors.push({ field: `virtualJacket[${index}].reportedGross`, message: 'Reported Gross is required' });
      }
      if (!jacket.reportedNet || jacket.reportedNet <= 0) {
        errors.push({ field: `virtualJacket[${index}].reportedNet`, message: 'Reported Net is required' });
      }
      if (!jacket.rateTypeId) {
        errors.push({ field: `virtualJacket[${index}].rateTypeId`, message: 'Rate Type is required' });
      }
      // Stat code is required for TX, NM, NY
      if (statCodeState.includes(calcCriteriaData.propertyState) && !jacket.statCode) {
        errors.push({ field: `virtualJacket[${index}].statCode`, message: 'Stat Code is required' });
      }
    });
    setVirtualJacketValidationError(errors);
  }, [virtualJackets, calcCriteriaData.propertyState]);

  // Notify parent component of validation changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(seValidationError, cplValidationError, virtualJacketValidationError);
    }
  }, [seValidationError, cplValidationError, virtualJacketValidationError, onValidationChange]);

  // Check policy inventory for virtual jacket feature
  useEffect(() => {
    if (calcCriteriaData.accountNumber) {
      executeGetPolicyInventory(calcCriteriaData.accountNumber);
    }
  }, [calcCriteriaData.accountNumber]);

  useEffect(() => {
    if (getPolicyInventoryStatus === 'success' && getPolicyInventoryValue) {
      setShowVirtualJacketButton(getPolicyInventoryValue.length > 0);
      setPolicyInventory(getPolicyInventoryValue);
    }
  }, [getPolicyInventoryStatus, getPolicyInventoryValue]);

  const renderFieldComponent = (
    field: any,
    type: string,
    validationError?: any,
    index?: any,
    subIndex?: any,
    filterRateTypeByExtended?: boolean,
  ) => {
    switch (field.dataType) {
      case 'date':
        return (
          <Grid item sm={field.size == '1 of 4' ? 3 : field.size == '2 of 4' ? 6 : field.size == '3 of 4' ? 9 : 12}>
            <DateFieldString
              label={field.label}
              name={field.name}
              qaAttribute={'CalcCriteria' + field.name}
              maxDate="12/31/9999"
              shouldPrioritizeOnChange
              value={(() => {
                const dateParsed = Date.parse(field.value || '');
                const currentDate = dateParsed ? formatDate(dateParsed, 'MM/dd/yyyy') : field.value;
                return currentDate ? currentDate : undefined;
              })()}
              onChange={(val: any) => {
                updateCalcCriteriaData(type, field.name, val, index, subIndex);
              }}
              disabled={field.isDisabled || disableFields}
              required={field.isRequired}
              errs={validationError}
              showValidation={shouldShowValidation}
            />
          </Grid>
        );
      case 'select': {
        const options = () => {
          const currentOption =
            field.name === 'StatCode'
              ? statCodeOptionList(index)
              : field?.options?.map((x: any, i: any) => {
                  return { ...x, name: x.text, value: x.value };
                });

          if (field.name === 'RateType' && currentOption.find((rate: PolicyFieldOption) => rate.isExtendedCoverage)) {
            return currentOption.filter(
              (rate: PolicyFieldOption) => rate.isExtendedCoverage === filterRateTypeByExtended,
            );
          }

          return currentOption;
        };

        return (
          <Grid item sm={field.size == '1 of 4' ? 3 : field.size == '2 of 4' ? 6 : field.size == '3 of 4' ? 9 : 12}>
            <AgentNetDropdownSelector
              label={field.label}
              name={field.name}
              qaAttribute={'CalcCriteria' + field.name}
              options={options()}
              pdfIcon={field.name === 'StatCode'}
              pdfLabel={'Stat Codes'}
              onPdfClick={openStatCodePDF}
              value={field.value}
              menuOption={(val: string) => {
                updateCalcCriteriaData(type, field.name, val, index, subIndex);
              }}
              dropdowntype="outlined"
              disabled={field.isDisabled || disableFields}
              required={field.isRequired}
              errs={validationError}
              showValidation={shouldShowValidation}
            />
          </Grid>
        );
      }
      case 'currency':
        return (
          <Grid item sm={field.size == '1 of 4' ? 3 : field.size == '2 of 4' ? 6 : field.size == '3 of 4' ? 9 : 12}>
            <CurrencyField
              variant="outlined"
              fullWidth
              label={field.label}
              allowedBlank
              name={field.name}
              id={field.name}
              data-testid={'CalcCriteria' + field.name}
              value={field.value ?? ''}
              max={100000000000}
              allowNegative={false}
              min={0}
              onChange={(e) => {
                updateCalcCriteriaData(
                  type,
                  field.name,
                  e.target.value === '' ? '' : (e.target.value as unknown as number).toFixed(2),
                  index,
                  subIndex,
                );
              }}
              disabled={field.isDisabled || disableFields}
              required={field.isRequired}
              errs={validationError}
              alwaysDisplayError={shouldShowValidation}
              showValidationOnBlur={shouldShowValidation}
            />
          </Grid>
        );
      case 'checkbox':
        return (
          <Grid
            item
            className={classes.paddingTopZero}
            sm={field.size == '1 of 4' ? 3 : field.size == '2 of 4' ? 6 : field.size == '3 of 4' ? 9 : 12}
          >
            <AgentNetCheckBox
              label={field.label}
              name={field.name}
              data-testid={'CalcCriteria' + field.name}
              value={field.isChecked}
              checkHandler={(val: any) => {
                updateCalcCriteriaData(type, field.name, val, index, subIndex, true);
              }}
              disabled={field.isDisabled || disableFields}
              required={field.isRequired}
            />
          </Grid>
        );
      default:
        return;
    }
  };

  const isReissueRateType = (index: any) => {
    const rateType = calcCriteriaData.jacketRates[index].fields.filter((item: any) => item.name === 'RateType')[0];
    const currentOption = rateType.options.filter((item: any) => item.value === rateType.value)[0];
    const savedRateType = savedJacketData?.[index]?.fields?.filter((item: any) => item.name === 'RateType')?.[0] ?? '';
    const savedOption = savedRateType
      ? savedRateType.options.filter((item: any) => item.value === savedRateType.value)[0]
      : '';

    if (savedOption?.text?.toLowerCase()?.includes('reissue')) {
      return false;
    }
    return currentOption?.text?.toLowerCase()?.includes('reissue') ?? false;
  };

  const callbacks = {
    updateCalcCriteriaData,
    renderFieldComponent,
    updateJacketEndorsement,
    openStatCodePDF,
    additionalQuestionRef,
  };

  const validateStandAloneEndorsement = async (data: any) => {
    const errs: FieldValidationError[] = (await doValidate(data, CalcCriteriaStandaloneSchema)) || [];
    setSEValidationError(errs);
  };

  useEffect(() => {
    if (
      calcCriteriaData?.standAloneEndorsementRates?.endorsements ||
      calcCriteriaData?.standAloneEndorsementRates?.questions
    ) {
      validateStandAloneEndorsement(calcCriteriaData?.standAloneEndorsementRates);
    }
  }, [calcCriteriaData?.standAloneEndorsementRates]);

  useEffect(() => {
    if (calcCriteriaData.fields) {
      calcCriteriaData.fields.forEach((field: any) => {
        if (field.name === 'RateEffectiveDate') {
          setRateEffectiveDate(field.value ?? '');
        }
      });
      setLoadEndorsement(true);
    }
  }, [calcCriteriaData.fields]);

  useEffect(() => {
    if (loadEndorsement) {
      const currentRateEffectiveDate =
        rateEffectiveDate && isValid(parse(rateEffectiveDate, 'MM/dd/yyyy', new Date())) ? rateEffectiveDate : '';
      fetchEndorsements(calcCriteriaData.propertyState, calcCriteriaData.underwriterCode, currentRateEffectiveDate);
    }
  }, [rateEffectiveDate, loadEndorsement]);

  useEffect(() => {
    if (getCplTypesStatus === 'idle' || getCplTypesStatus === 'error') {
      executeGetCplTypes(calcCriteriaData.accountNumber);
    }
  }, [calcCriteriaData.accountNumber]);

  useEffect(() => {
    if (getCplTypesStatus === 'success' && getCplTypesValue) {
      setCplTypes(getCplTypesValue);
    }
  }, [getCplTypesStatus, getCplTypesValue]);

  // Determine if standalone endorsement section should be visible
  const hasStandaloneEndorsements = !!calcCriteriaData?.standAloneEndorsementRates?.endorsements;
  const hasNoPolicies = !calcCriteriaData?.jacketRates || calcCriteriaData?.jacketRates?.length === 0;
  const hasCpl = calcCriteriaData?.cplRates?.length > 0;
  const isCplOnlyFile = hasNoPolicies && hasCpl;
  const shouldShowStandaloneEndorsements = hasStandaloneEndorsements || isCplOnlyFile;

  return (
    <Grid className={classes.root}>
      <Grid container spacing={2}>
        {calcCriteriaData?.fields?.length > 0 && (
          <Grid item sm={12} className={classes.heading}>
            <AgentNetDivider typoVariant="h2" title={'File Level Settings'} disablePadding />
          </Grid>
        )}
        {calcCriteriaData?.fields?.length > 0 &&
          calcCriteriaData?.fields?.map((field: any) => (
            <>{renderFieldComponent(field, 'updateFileLevelSettings', fileLevelValidationError)}</>
          ))}

        {calcCriteriaData?.jacketRates?.length > 0 &&
          calcCriteriaData?.jacketRates.map((item: any, index: number) => {
            return (
              <Policy
                key={`${item.policyNumber}-${index}`}
                index={index}
                item={item}
                validationErrors={validationErrors[index] ?? []}
                shouldShowValidation={shouldShowValidation}
                callbacks={callbacks}
                isReissue={isReissueRateType(index)}
                endorsementsList={endorsementType}
                propertyState={calcCriteriaData.propertyState}
                disableFields={disableFields}
                rateEffectiveDate={rateEffectiveDate}
                underwriterCode={calcCriteriaData.underwriterCode}
              />
            );
          })}

        {shouldShowStandaloneEndorsements && (
          <>
            <Grid item sm={12} className={classes.heading}>
              <AgentNetDivider typoVariant="h3" title={'Standalone Endorsements'} disablePadding />
            </Grid>
            <Grid container className={classes.endorsement}>
              <Endorsements
                isEditDisabled={disableFields}
                onChangeEndorsement={(...args) => updateSaEndorsement(...args)}
                endorsements={calcCriteriaData?.standAloneEndorsementRates?.endorsements}
                endorsementOptions={endorsementType}
                validationErrors={seValidationError}
                openStatCodePDF={openStatCodePDF}
                shouldDisableScroll
                shouldShowValidation={shouldShowValidation}
                showStatCode={statCodeState.includes(calcCriteriaData?.propertyState)}
              />
            </Grid>
          </>
        )}
        {calcCriteriaData?.standAloneEndorsementRates?.questions?.length > 0 && (
          <Grid container spacing={2} className={classes.marginZero}>
            <Grid item sm={12} className={classes.heading}>
              <AgentNetDivider typoVariant="h3" title={'Additional Questions'} disablePadding />
            </Grid>
            <Grid item sm={12}>
              {calcCriteriaData?.standAloneEndorsementRates?.questions.map((question: any, index: number) => (
                <QuestionItem
                  {...question}
                  dataTestId={`Standalone${index}AdditionalQuestion`}
                  additionalQuestionRef={additionalQuestionRef}
                  validationErrors={seValidationError}
                  isDisabled={disableFields || question?.isDisabled}
                  shouldShowValidation={shouldShowValidation}
                  key={question.randomId}
                  onChange={(val: any) => {
                    updateCalcCriteriaData('updateStandAloneQuestions', 'standAloneEndorsementRates', val, index);
                  }}
                />
              ))}
            </Grid>
          </Grid>
        )}
        {showVirtualJacketButton && (
          <>
            {virtualJackets.length === 0 ? (
              <Grid container spacing={2} className={classes.marginZero}>
                <Grid item sm={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <AgentNetButton
                    color="primary"
                    size="small"
                    variant="contained"
                    onClick={handleAddVirtualJacket}
                    disabled={disableFields}
                    plusIcon
                  >
                    Add Virtual Jacket
                  </AgentNetButton>
                </Grid>
              </Grid>
            ) : (
              <Card className={classes.sectionCard}>
                <Grid container spacing={2} className={classes.marginZero}>
                  <Grid item sm={8}>
                    <div className={classes.sectionTitle}>Virtual Jackets</div>
                  </Grid>
                  <Grid item sm={4} className={classes.addVirtualJacketButton}>
                    <AgentNetButton
                      color="primary"
                      size="small"
                      variant="contained"
                      onClick={handleAddVirtualJacket}
                      disabled={disableFields}
                      plusIcon
                    >
                      Add Virtual Jacket
                    </AgentNetButton>
                  </Grid>
                </Grid>
                <Divider />
                <CardContent className={classes.sectionCardContent}>
                  {virtualJackets.map((jacket, index) => (
                    <CardContent key={index}>
                      <VirtualJacketCard
                        key={jacket.id}
                        jacket={jacket}
                        policyInventory={policyInventory}
                        stateCode={calcCriteriaData.propertyState}
                        countyCode={calcCriteriaData.propertyCounty}
                        accountNumber={calcCriteriaData.accountNumber}
                        selectedPolicies={virtualJackets.map((j) => j.policyNumber)}
                        onChange={(updatedJacket) => handleUpdateVirtualJacket(index, updatedJacket)}
                        onDelete={() => handleDeleteVirtualJacket(index)}
                        validationErrors={validationErrors}
                      />
                    </CardContent>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
        <Grid container spacing={2} className={classes.marginZero}>
          <Grid item sm={9} className={classes.heading}>
            {calcCriteriaData?.cplRates?.length > 0 && (
              <AgentNetDivider typoVariant="h2" title={'Closing Protection Letters'} disablePadding />
            )}
          </Grid>
          <Grid item sm={1} />
          <Grid item sm={2} className={classes.heading}>
            {canUseAddCplFeeToFile && (
              <AgentNetButton
                color="primary"
                size="small"
                variant="contained"
                onClick={() => {
                  const cplFee: CplType = {
                    id: makeRandomId(),
                    cplGroupId: 0,
                    cplGroupDesc: '',
                    coveredPartyTypeCdId: 0,
                    coveredPartyTypeDesc: '',
                    stateCode: calcCriteriaData.stateCode,
                    underwriterId: 0,
                    cplId: 0,
                    cplDesc: '',
                    starsItemNumber: '',
                    fee: 0,
                    feeInput: '',
                  };
                  setCplFees([...cplFees, cplFee]);
                }}
                disabled={cplTypes.length === 0 || disableFields}
                fullWidth={true}
                plusIcon
              >
                Add CPL Fee
              </AgentNetButton>
            )}
          </Grid>
          {canUseAddCplFeeToFile && cplFees.length > 0 ? (
            <div style={{ height: 200, width: '100%' }}>
              <DataTable
                rowData={cplFees}
                columnDefs={cplFeesColumnConfig}
                animateRows={false}
                noRowsOverlayComponent="customOverlayNoRows"
                stopEditingWhenCellsLoseFocus={true}
                rowHeight={Number(pxToRem(40))}
                suppressRowHoverHighlight={true}
                columnHoverHighlight={false}
                rowClass=""
                rowStyle={{ background: 'none' }}
              />
            </div>
          ) : null}
          <CPLCalcTable
            letters={calcCriteriaData?.cplRates ?? []}
            propertyState={calcCriteriaData?.propertyState}
            updateCalcCriteriaData={updateCalcCriteriaData}
            cplValidationError={cplValidationError}
            setCplValidationError={setCplValidationError}
            shouldShowValidation={shouldShowValidation}
            disableFields={disableFields}
          />
          {calcCriteriaData.propertyState === 'OH' &&
            calcCriteriaData?.cplRates.flatMap((element: any) => element.questions).length > 0 && (
              <Grid item sm={12} className={classes.heading}>
                <AgentNetDivider typoVariant="h3" title={'Additional Questions'} disablePadding />
              </Grid>
            )}
          <Grid item sm={12}>
            {calcCriteriaData?.propertyState === 'OH' &&
              calcCriteriaData?.cplRates.map((element: any, index: number) =>
                (element?.questions ?? []).map((question: any, subindex: number) => (
                  <QuestionItem
                    {...question}
                    dataTestId={`CPL${index}AdditionalQuestion${subindex}`}
                    isDisabled={disableFields || question?.isDisabled}
                    additionalQuestionRef={additionalQuestionRef}
                    key={question.randomId}
                    onChange={(val: any) => {
                      updateCalcCriteriaData('updateQuestions', 'cplRates', val, index, subindex);
                    }}
                    validationErrors={cplValidationError[index] ?? []}
                    shouldShowValidation={shouldShowValidation}
                  />
                )),
              )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
export default CalcCriteriaTab;
